/**
 * Load-more pagination for the "Collection + load more" section.
 *
 * Fetches the next page of the section via the Section Rendering API, so only
 * the section markup is returned rather than the whole page. The response is
 * parsed and its product cards are appended to the existing grid.
 *
 * Usage:
 *  {%- paginate collection.products by section.settings.products_per_page -%}
 *    <featured-collection-more data-infinite-scroll="false">
 *      <ul class="js-fc-results">
 *        <li class="js-fc-result">...</li>
 *      </ul>
 *      {%- if paginate.next.is_link -%}
 *        <nav class="js-fc-pagination">
 *          <a class="js-fc-load-more" href="...&section_id=x&page=2">View more</a>
 *        </nav>
 *      {%- endif -%}
 *    </featured-collection-more>
 *  {%- endpaginate -%}
 */
if (!customElements.get('featured-collection-more')) {
  class FeaturedCollectionMore extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.results = this.querySelector('.js-fc-results');
      this.clickHandler = this.handleClick.bind(this);
      this.addEventListener('click', this.clickHandler);

      this.observePagination();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.clickHandler);
      if (this.observer) this.observer.disconnect();
    }

    get pagination() {
      return this.querySelector('.js-fc-pagination');
    }

    get loadMoreButton() {
      return this.querySelector('.js-fc-load-more');
    }

    /**
     * Watches the pagination element so more results load as it scrolls into
     * view. Only active when infinite scroll is enabled on the section.
     */
    observePagination() {
      if (this.dataset.infiniteScroll !== 'true' || !this.pagination) return;

      if (!this.observer) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) this.loadMore();
          },
          { rootMargin: `${window.innerHeight}px 0px` }
        );
      }

      // The nav is replaced on every load, so stop watching the discarded one
      // rather than accumulating a detached node per page.
      if (this.observed && this.observed !== this.pagination) {
        this.observer.unobserve(this.observed);
      }

      this.observed = this.pagination;
      this.observer.observe(this.pagination);
    }

    /**
     * Handles a click on the "View more" button.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (!evt.target.closest('.js-fc-load-more')) return;
      evt.preventDefault();
      this.loadMore();
    }

    /**
     * Fetches the next page of products and appends them to the grid.
     */
    async loadMore() {
      const button = this.loadMoreButton;
      if (this.isFetching || !button || !this.results) return;

      this.isFetching = true;
      button.classList.add('is-loading');
      button.setAttribute('aria-disabled', 'true');

      try {
        const response = await fetch(button.href);
        if (!response.ok) throw new Error(response.status);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        const newResults = tmpl.content.querySelectorAll('.js-fc-result');
        if (newResults.length === 0) throw new Error('No further results returned.');

        let html = '';
        newResults.forEach((result) => {
          html += result.outerHTML;
        });
        this.results.insertAdjacentHTML('beforeend', html);

        // Swap in the next page's pagination, or remove it when we're at the end.
        const newPagination = tmpl.content.querySelector('.js-fc-pagination');
        if (newPagination) {
          this.pagination.replaceWith(newPagination);
          this.observePagination();
        } else {
          if (this.observer) this.observer.disconnect();
          this.pagination.remove();
        }

        this.dispatchEvent(
          new CustomEvent('on:featured-collection:loaded', { bubbles: true, detail: { count: newResults.length } })
        );
      } catch (error) {
        console.log('Unable to fetch the next page of products', error); // eslint-disable-line no-console
        if (this.observer) this.observer.disconnect();
        button.classList.remove('is-loading');
        button.removeAttribute('aria-disabled');
      } finally {
        this.isFetching = false;
      }
    }
  }

  customElements.define('featured-collection-more', FeaturedCollectionMore);
}
